import moment from 'moment/moment'

import { PSApplication } from '../components/app/PSApplication'
import { DataType } from '../types/DataType'
import { ConstructorArgs } from '../types/models'
import { BaseRecord } from './BaseRecord'
import { Booking } from './Booking'
import { Estimate } from './Estimate'
import { Image } from './Image'
import { Location } from './Location'
import { Order } from './Order'
import { FKType } from './SQLBuilder'
import { Subaccount } from './Subaccount'
import { User } from './User'

type NoteConstructorArgs = ConstructorArgs<() => Note>

type NoteRecordType = 'estimate' | 'booking' | 'user'

export class Note extends BaseRecord {
    readonly recordType = 'note'

    note: string = ''
    title?: string
    image?: Image
    imageUri?: string
    cancelledDate?: moment.Moment
    lastModifiedDate?: moment.Moment

    constructor(data?: Partial<NoteConstructorArgs>) {
        super(data)
        if (!data) {
            this.creator = PSApplication.me()
            this.owner = this.creator.owner
            return
        }

        this.note = data.note ?? ''
        this.title = data.title ?? undefined
        this.image = this.propertyFromImageArg(data.image, data.image_uri)
        this.imageUri = data.image_uri ?? undefined
        this.cancelledDate = this.propertyFromTimestampArg(data.cancelled_date)
        this.lastModifiedDate = this.propertyFromTimestampArg(data.last_modified_date)
    }

    clone(overrides?: Partial<NoteConstructorArgs>): Note {
        return new Note({ ...this.serializeForClone(), ...overrides })
    }

    get type(): NoteRecordType {
        return this.estimate ? 'estimate' : this.booking ? 'booking' : 'user'
    }

    deserilaize(data: any): void {
        super.deserialize(data)

        if (data.owner) this.owner = BaseRecord.fromSerializer(data.owner, User)
        if (data.creator) this.creator = BaseRecord.fromSerializer(data.creator, User)
        if (data.person) this.person = BaseRecord.fromSerializer(data.person, User)
        if (data.booking) this.booking = BaseRecord.fromSerializer(data.booking, Booking)
        if (data.location) this.location = BaseRecord.fromSerializer(data.location, Location)
        if (data.subaccount)
            this.subaccount = BaseRecord.fromSerializer(data.subaccount, Subaccount)
        if (data.order) this.order = BaseRecord.fromSerializer(data.order, Order)
        if (data.estimate) this.estimate = BaseRecord.fromSerializer(data.estimate, Estimate)
        if (data.image) this.image = BaseRecord.fromSerializer(data.image, Image)
        if (data.cancelledDate) this.cancelledDate = moment(data.cancelledDate)
        if (data.lastModifiedDate) this.lastModifiedDate = moment(data.lastModifiedDate)
    }

    jsonObject(): any {
        const result = super.jsonObject()
        if (this.person) result['person'] = this.person.id
        if (this.booking) result['booking'] = this.booking.id
        if (this.location) result['location'] = this.location.id
        if (this.estimate) result['estimate'] = this.estimate.id
        if (this.order) result['order'] = this.order.id
        if (this.subaccount) result['subaccount'] = this.subaccount.id
        result['title'] = this.title ?? ''
        result['note'] = this.note ?? ''
        return result
    }

    static fk: FKType = {
        users: ['owner', 'creator', 'person'],
        bookings: ['booking'],
        estimates: ['estimate'],
        locations: ['location'],
        orders: ['order'],
        subaccounts: ['subaccount'],
    }

    static dataType(): DataType {
        return 'notes'
    }

    get owner(): User | undefined {
        return this.getForeignRecord('owner', User)
    }
    set owner(owner: User | undefined) {
        this.setForeignRecord('owner', owner)
    }
    get creator(): User | undefined {
        return this.getForeignRecord('creator', User)
    }
    set creator(creator: User | undefined) {
        this.setForeignRecord('creator', creator)
    }
    get person(): User | undefined {
        return this.getForeignRecord('person', User)
    }
    set person(person: User | undefined) {
        this.setForeignRecord('person', person)
    }
    get booking(): Booking | undefined {
        return this.getForeignRecord('booking', Booking)
    }
    set booking(booking: Booking | undefined) {
        this.setForeignRecord('booking', booking)
    }
    get location(): Location | undefined {
        return this.getForeignRecord('location', Location)
    }
    set location(location: Location | undefined) {
        this.setForeignRecord('location', location)
    }
    get subaccount(): Subaccount | undefined {
        return this.getForeignRecord('subaccount', Subaccount)
    }
    set subaccount(subaccount: Subaccount | undefined) {
        this.setForeignRecord('subaccount', subaccount)
    }
    get order(): Order | undefined {
        return this.getForeignRecord('order', Order)
    }
    set order(order: Order | undefined) {
        this.setForeignRecord('order', order)
    }
    get estimate(): Estimate | undefined {
        return this.getForeignRecord('estimate', Estimate)
    }
    set estimate(estimate: Estimate | undefined) {
        this.setForeignRecord('estimate', estimate)
    }
}
